import {styled} from "@mui/material/styles"
import {Button, Typography} from "@mui/material"
import Box from "@mui/material/Box"
import {useTranslation, Trans} from "react-i18next"
import {WarningOctagonIcon} from "@indebted/assets"
import {paletteColors} from "@indebted/theme"
import {Panel} from "@indebted/components/Panel"
import {NewLayoutPage} from "@indebted/components/Page"

import {formatCurrency} from "../../../src/currencyFormatter"
import {formatDate} from "../../../src/dateFormatter"
import {usePaymentArrangementInitiateSetup} from "../usePaymentArrangementInitiateSetup"
import {ValidLocale} from "../../../src/Locales"

import {useStatusPageData} from "./index"

export function CancelledByAgentPage() {
	const data = useStatusPageData()
	const {CustomerName} = data.data
	const {t} = useTranslation()

	return (
		<NewLayoutPage>
			<Header title={t("payment_arrangement_status.cancelled_by_agent.title")} />

			<Box padding="1.5rem 1.5rem 4rem 1.5rem">
				{/* Greeting */}
				<Typography
					textAlign="start"
					variant="body1"
					sx={{fontSize: "0.9375rem", fontWeight: "500", color: "#000244"}}
				>
					{t("payment_arrangement_status.cancelled_by_agent.greeting", {
						CustomerName: CustomerName,
					})}
				</Typography>
				{/* Description Message */}
				<Typography
					textAlign="start"
					variant="body1"
					sx={{fontSize: "0.9375rem", fontWeight: "500", color: "#000244"}}
				>
					{t("payment_arrangement_status.cancelled_by_agent.message")}
				</Typography>
				{/* Payment Details Card */}
				<Box sx={{paddingTop: "1.5rem"}}>
					<PaymentArrangementCancelledDetails />
				</Box>
			</Box>
		</NewLayoutPage>
	)
}

export function PaymentArrangementCancelledDetails() {
	const {t, i18n} = useTranslation()
	const locale = i18n.language as ValidLocale

	const {data} = useStatusPageData()
	const {InstalmentAmountValue, InstalmentAmountCurrency, FrequencyValue, StartDateValue, EndDateValue, ClientName} =
		data

	return (
		<Panel>
			<Typography
				variant="h6"
				component="h2"
				sx={{fontSize: "1rem", fontWeight: "700", lineHeight: "1.5rem", paddingBottom: "0.25rem"}}
			>
				{t("payment_arrangement_status.cancelled_by_agent.details.title")}
			</Typography>

			<DetailRow>
				<DetailLabel variant="body2">
					{t("payment_arrangement_status.cancelled_by_agent.details.payment_amount.label")}
				</DetailLabel>
				<DetailValue variant="body2">
					{formatCurrency(InstalmentAmountValue, InstalmentAmountCurrency, locale)}
				</DetailValue>
			</DetailRow>

			<DetailRow>
				<DetailLabel variant="body2">
					{t("payment_arrangement_status.cancelled_by_agent.details.frequency.label")}
				</DetailLabel>
				<DetailValue variant="body2">
					{t(`payment_arrangement_status.cancelled_by_agent.details.frequency.value.${FrequencyValue}`)}
				</DetailValue>
			</DetailRow>

			<DetailRow>
				<DetailLabel variant="body2">
					{t("payment_arrangement_status.cancelled_by_agent.details.start_date.label")}
				</DetailLabel>
				<DetailValue variant="body2">{formatDate(new Date(StartDateValue))}</DetailValue>
			</DetailRow>

			<DetailRow>
				<DetailLabel variant="body2">
					{t("payment_arrangement_status.cancelled_by_agent.details.paid_to.label")}
				</DetailLabel>
				<DetailValue variant="body2" className="amp-mask">
					{ClientName}
				</DetailValue>
			</DetailRow>

			<Typography
				variant="body1"
				sx={{
					color: "#000244",
					fontSize: "0.75rem",
					fontWeight: "500",
					lineHeight: "1.125rem",
					marginTop: "1rem",
					textAlign: "center",
				}}
			>
				<Trans i18nKey="payment_arrangement_status.cancelled.setup_new.footer" components={{a: <a />}} />
			</Typography>
		</Panel>
	)
}

export function Header({title}: {title: string}) {
	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				gap: "0.625rem",
				padding: "3rem 1.5rem 1.5rem 1.5rem",
				backgroundColor: "#F6F6F6",
			}}
		>
			<WarningOctagonIcon />
			<Typography align="center" component="h1" sx={{fontSize: "1.875rem", fontWeight: "700", color: "#000244"}}>
				{title}
			</Typography>
		</Box>
	)
}

const DetailRow = styled("div")`
	color: #000244;
	font-size: 1rem;
	font-weight: 700;
	line-height: 1.5rem;
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding-top: 0.75rem;
	padding-bottom: 0.5rem;
	border-bottom: 0.0625rem solid #f6f6f6;
`

const DetailLabel = styled(Typography)`
	color: #000244;
	font-size: 0.875rem;
	font-weight: 400;
	line-height: 1.25rem;
`

const DetailValue = styled(Typography)`
	color: #000244;
	font-size: 0.875rem;
	font-weight: 700;
	line-height: 1.25rem;
`
